<template>
  <div class="card sales-performance-card">
    <div class="card-hdr">
      <div class="sp-hdr-left">
        <span class="card-ttl">Sales Performance</span>
        <div class="sp-toggle-group">
          <button
            v-for="opt in chartTypeOptions"
            :key="opt.key"
            type="button"
            class="sp-toggle-btn"
            :class="{ active: chartType === opt.key }"
            @click="chartType = opt.key"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      <div class="sp-toggle-group">
        <button
          v-for="opt in toggleOptions"
          :key="opt.key"
          type="button"
          class="sp-toggle-btn"
          :class="{ active: selectedMetric === opt.key }"
          @click="selectedMetric = opt.key"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="sp-state">
        <DxLoadIndicator :height="24" :width="24" />
      </div>

      <div v-else-if="hasError" class="sp-state sp-error">
        <div class="sp-error-msg">Unable to load Sales Performance data.</div>
        <DxButton text="Retry" @click="load" />
      </div>

      <div v-else class="sp-chart-wrap">
        <DxChart
          :data-source="chartData"
          palette="Soft"
          :redraw-on-resize="true"
        >
          <DxCommonSeriesSettings
            argument-field="month"
            :type="seriesType"
            hover-mode="allArgumentPoints"
          />
          <DxSeries
            value-field="lastYear"
            :name="lastYearLabel"
            color="#c4d4e1"
          />
          <DxSeries
            value-field="thisYear"
            :name="thisYearLabel"
            color="#35baf6"
          />
          <DxArgumentAxis :allow-decimals="false" />
          <DxValueAxis :value-margins-enabled="false" />
          <DxLegend
            vertical-alignment="bottom"
            horizontal-alignment="center"
            :margin="4"
          />
          <DxTooltip :enabled="true" :customize-tooltip="customizeTooltip" />
        </DxChart>
      </div>
    </div>
  </div>
</template>

<script>
import { DxButton } from "devextreme-vue/button";
import { DxLoadIndicator } from "devextreme-vue/load-indicator";
import {
  DxChart,
  DxSeries,
  DxArgumentAxis,
  DxValueAxis,
  DxLegend,
  DxCommonSeriesSettings,
  DxTooltip,
} from "devextreme-vue/chart";
import { CustomerPerformanceService } from "../../_services/api/customers/customer-performance.service";

export default {
  name: "GcDashboardSalesPerformanceCard",
  components: {
    DxButton,
    DxLoadIndicator,
    DxChart,
    DxSeries,
    DxArgumentAxis,
    DxValueAxis,
    DxLegend,
    DxCommonSeriesSettings,
    DxTooltip,
  },
  props: {
    customerCode: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      hasError: false,
      response: null,
      selectedMetric: "GrossValue",
      toggleOptions: [
        { key: "GrossValue", label: "Sales $" },
        { key: "MarginValue", label: "GP $" },
        { key: "MarginPcnt", label: "GP %" },
      ],
      chartType: "column",
      chartTypeOptions: [
        { key: "column", label: "Column" },
        { key: "spline", label: "Spline Area" },
      ],
    };
  },
  computed: {
    seriesType() {
      return this.chartType === "column" ? "bar" : "splinearea";
    },
    thisYearLabel() {
      return this.formatFyLabel(this.response && this.response.ThisFiscalYear) || "This Year";
    },
    lastYearLabel() {
      return this.formatFyLabel(this.response && this.response.LastFiscalYear) || "Prior Year";
    },
    chartData() {
      if (!this.response) return [];

      const thisYearRows = this.response.ThisYear || [];
      const lastYearRows = this.response.LastYear || [];

      const thisYearByMonth = new Map(
        thisYearRows.map((r) => [r.MonthSortOrder, r])
      );
      const lastYearByMonth = new Map(
        lastYearRows.map((r) => [r.MonthSortOrder, r])
      );

      const sortOrders = new Set([
        ...thisYearByMonth.keys(),
        ...lastYearByMonth.keys(),
      ]);

      return Array.from(sortOrders)
        .sort((a, b) => a - b)
        .map((sortOrder) => {
          const t = thisYearByMonth.get(sortOrder);
          const l = lastYearByMonth.get(sortOrder);
          const monthLabel = ((t && t.Month) || (l && l.Month) || "").trim();
          return {
            month: monthLabel,
            thisYear: t ? Number(t[this.selectedMetric]) : null,
            lastYear: l ? Number(l[this.selectedMetric]) : null,
          };
        });
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.hasError = false;
      try {
        const res = await CustomerPerformanceService.getMonthly(
          this.customerCode
        );
        this.response = res.data || {
          ThisFiscalYear: null,
          LastFiscalYear: null,
          ThisYear: [],
          LastYear: [],
        };
      } catch (err) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async refresh() {
      await this.load();
    },
    formatFyLabel(fiscalYear) {
      if (!fiscalYear) return null;
      const yearNum = parseInt(fiscalYear, 10);
      if (isNaN(yearNum)) return null;
      const prevYear = yearNum - 1;
      const yearShort = String(yearNum).slice(-2);
      return `FY ${prevYear}/${yearShort}`;
    },
    customizeTooltip(arg) {
      const value = arg.value;
      if (this.selectedMetric === "MarginPcnt") {
        return { text: `${arg.seriesName}: ${Number(value).toFixed(1)}%` };
      }
      const formatted = new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
      }).format(Number(value));
      return { text: `${arg.seriesName}: ${formatted}` };
    },
  },
};
</script>

<style lang="scss" scoped>
.sales-performance-card {
  background: #fff;
  border: 1px solid var(--base-border-color, #e0e0e0);
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  min-width: 0;

  .card-hdr {
    padding: 8px 12px;
    border-bottom: 1px solid var(--base-border-color, #edf0f3);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 8px;
  }

  .card-ttl {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
  }

  .sp-hdr-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .sp-toggle-group {
    display: inline-flex;
    background: rgba(53, 186, 246, 0.08);
    border-radius: 9999px;
    padding: 3px;
  }

  .sp-toggle-btn {
    background: transparent;
    border: 0;
    border-radius: 9999px;
    padding: 6px 14px;
    font-size: 11px;
    font-weight: 500;
    color: #475467;
    cursor: pointer;
    font-family: Inter, Roboto, sans-serif;

    &.active {
      background: #35baf6;
      color: #fff;
    }

    &:hover:not(.active) {
      background: rgba(53, 186, 246, 0.14);
    }
  }

  .card-body {
    padding: 10px 12px;
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .sp-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 24px 0;
  }

  .sp-error {
    flex-direction: column;

    .sp-error-msg {
      color: #e8384f;
      font-size: 12px;
    }
  }

  .sp-chart-wrap {
    flex: 1;
    min-height: 220px;
  }
}
</style>
